import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { Button } from "@/core/components/ui/button";
import { Label } from "@/core/components/ui/label";
import { Stack } from "@/core/components/ui/stack";
import { Switch } from "@/core/components/ui/switch";
import { Typography } from "@/core/components/ui/typography";
import { formatMoney } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
import { PAYROLL_PERIOD_STATUS } from "@/spiffs/constants";
import { PayrollPeriodStatus } from "@/spiffs/spiffs.d";
import { LockPeriodButton } from "./lock-period-button";

interface PayrollPeriodHeaderProps {
  spiffCount: number;
  userCount: number;
  totalAmount: number;
  dismissedCount: number;
  isLocked: boolean;
  selectedCount: number;
  isFlatView: boolean;
  onToggleView: (isFlatView: boolean) => void;
  onDismissSelected: () => void;
  onExport: () => void;
  onLockPeriod: () => void;
  canLock: boolean;
  periodStatus: PayrollPeriodStatus;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export const PayrollPeriodHeader = ({
  spiffCount,
  userCount,
  totalAmount,
  dismissedCount,
  isLocked,
  selectedCount,
  isFlatView,
  onToggleView,
  onDismissSelected,
  onExport,
  onLockPeriod,
  canLock,
  periodStatus,
  isExpanded,
  onToggleExpanded,
}: PayrollPeriodHeaderProps) => {
  const isPastAndUnlocked =
    periodStatus === PAYROLL_PERIOD_STATUS.past && !isLocked;

  return (
    <div
      className={cn("p-4 transition-colors duration-300", {
        "border-b bg-background": isExpanded,
        "rounded-xl": !isExpanded,
      })}
    >
      <Stack direction="row" align="center" justify="between">
        <Stack direction="row" align="center" gap="md">
          <Button
            onClick={onToggleExpanded}
            variant="icon"
            size="icon"
            className={cn("bg-transparent hover:bg-surface-secondary", {
              "hover:bg-surface-primary": isExpanded,
            })}
            aria-label={isExpanded ? "Collapse period" : "Expand period"}
          >
            {isExpanded ? (
              <ChevronsDownUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronsUpDown className="h-5 w-5 text-muted-foreground" />
            )}
          </Button>
          <Typography variant="bodyW2" className="whitespace-nowrap">
            {spiffCount} spiff{spiffCount !== 1 ? "s" : ""}
          </Typography>
          <Typography variant="bodyW2" className="whitespace-nowrap">
            {userCount} technician{userCount !== 1 ? "s" : ""}
          </Typography>
          <Typography
            variant="bodyW2"
            textColor="primary"
            className="whitespace-nowrap font-semibold"
          >
            {formatMoney(totalAmount)}
          </Typography>
          {dismissedCount > 0 && (
            <Typography
              variant="bodyW2"
              textColor="tertiary"
              className="whitespace-nowrap"
            >
              ({dismissedCount} dismissed)
            </Typography>
          )}
        </Stack>

        <Stack direction="row" align="center" gap="sm">
          {isExpanded && selectedCount === 0 && (
            <Stack
              direction="row"
              align="center"
              gap="sm"
              className="pr-4 duration-300 animate-in fade-in slide-in-from-right-2"
            >
              <Label
                htmlFor="group-by-technician"
                className="whitespace-nowrap font-inter text-md font-normal text-muted-foreground"
              >
                Group by technician
              </Label>
              <Switch
                id="group-by-technician"
                checked={!isFlatView}
                onCheckedChange={(checked) => onToggleView(!checked)}
                className="h-6 w-12 [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-6 [&>span]:data-[state=unchecked]:translate-x-0"
              />
            </Stack>
          )}
          {isExpanded && selectedCount > 0 && !isLocked && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onDismissSelected}
              className="duration-300 animate-in fade-in slide-in-from-right-2"
            >
              Dismiss ({selectedCount})
            </Button>
          )}

          <Button variant="secondary" size="sm" onClick={onExport}>
            Export
          </Button>

          {isPastAndUnlocked && (
            <LockPeriodButton
              onClick={onLockPeriod}
              disabled={!canLock}
              disabledTooltip={
                !canLock
                  ? "You must lock all prior periods to be able to lock this one"
                  : undefined
              }
            />
          )}
        </Stack>
      </Stack>
    </div>
  );
};
